import type {
  ColDef,
  IDoesFilterPassParams,
  IFilterComp,
  IFilterParams,
} from 'ag-grid-community';
import { makeFilterBoxResizable } from './filterResize';

type CheckboxFilterModel = {
  values: string[];
};

type CheckboxFilterParams = IFilterParams & {
  colDef: ColDef;
  availableValues?: string[]; // Optional: if provided, use these instead of scanning grid
  // Value mapper: maps API values to display values (e.g., "True" -> "Yes", "False" -> "No")
  valueMapper?: (apiValue: string) => string;
  // Value extractor: extracts a part from values (e.g., extracts year from "1999-01-23")
  valueExtractor?: (apiValue: string) => string;
  // Reverse mapper: maps display values back to API values (e.g., "Yes" -> "True", "No" -> "False")
  reverseMapper?: (displayValue: string) => string;
  // If true, skip client-side filtering (server handles filtering)
  serverSideFiltering?: boolean;
  // If true, deduplicate values case-insensitively, keeping the first occurrence
  caseInsensitiveDeduplication?: boolean;
};

const BLANK_KEY = '__AG_CHECKBOX_FILTER_BLANK__';

// Helper function to deduplicate values case-insensitively, keeping the first occurrence
function deduplicateCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const lower = value.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      result.push(value);
    }
  }

  return result;
}

export class AgCheckboxFilter implements IFilterComp<CheckboxFilterModel> {
  private params!: CheckboxFilterParams;

  private gui!: HTMLElement;

  private selectedContainer!: HTMLElement;

  private listContainer!: HTMLElement;

  private searchInput!: HTMLInputElement;

  private resetButton!: HTMLButtonElement;

  private applyButton!: HTMLButtonElement;

  private cancelButton!: HTMLButtonElement;

  private applySavedSize?: () => void;

  private values: string[] = [];

  private filteredValues: string[] = [];

  /** Ticked in the popup but not yet committed. Nothing in this popup touches
   *  the table until Apply, so this is UI state only. */
  private pendingValues: Set<string> = new Set();

  /** Committed selection — the filter model. `getModel`, `isFilterActive` and
   *  `doesFilterPass` read this and never `pendingValues`. */
  private appliedValues: Set<string> = new Set();

  /** Which values were in the "Selected:" box the last time we partitioned.
   *  Ticking a box deliberately does NOT update this: an option that jumps to
   *  the top the instant you tick it moves the row under the cursor, which
   *  makes picking a second option a game of whack-a-mole. Recomputed only at
   *  moments the cursor isn't mid-click-run — popup open/close, search change,
   *  Apply, Reset, and programmatic `setModel`. */
  private partitionedValues: Set<string> = new Set();

  /** Whether the popup is currently on screen, so a background refresh of the
   *  available values doesn't reshuffle a list being clicked. */
  private guiAttached = false;

  public init(params: CheckboxFilterParams): void {
    this.params = params;
    this.gui = document.createElement('div');
    this.gui.className =
      'ag-checkbox-filter tw-flex tw-flex-col tw-gap-2 tw-p-2 tw-text-sm';

    this.createSearchInput();
    this.createSelectedContainer();
    this.createResetButton();
    this.createListContainer();
    this.createFooterButtons();

    // The option list can grow well beyond 250px of content, so let the user
    // resize the box (persisted per user, keyed by column so each filter keeps
    // its own size). The list container is the element that grows to fill the
    // box once a height is applied.
    this.applySavedSize = makeFilterBoxResizable(this.gui, {
      fillElement: this.listContainer,
      storageKey: `westbury.masterDiscography.filterBoxSize.${params.column.getColId()}`,
    });

    this.updateAvailableValues();
  }

  public getGui(): HTMLElement {
    return this.gui;
  }

  public doesFilterPass(params: IDoesFilterPassParams): boolean {
    // If server-side filtering is enabled, always return true (server handles filtering)
    if (this.params.serverSideFiltering) {
      return true;
    }

    // If no values are selected, show all rows (filter is inactive)
    if (!this.isFilterActive()) {
      return true;
    }

    // Skip filtering for detail rows - they should always show if their parent passes
    if (params.node.data && (params.node.data as any).kind === 'detail') {
      return true;
    }

    const value = this.getValue(params);

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return this.appliedValues.has(BLANK_KEY);
    }

    // Check if value is an array (for fields like contributions that are arrays)
    if (Array.isArray(value)) {
      // For arrays, check if any item in the array matches any selected value
      return value.some((item) => {
        const normalized = this.normalizeValue(item);
        return normalized !== null && this.appliedValues.has(normalized);
      });
    }

    // Handle comma-separated strings (like "Producer, Writer")
    const normalized = this.normalizeValue(value);
    if (normalized === null) {
      return this.appliedValues.has(BLANK_KEY);
    }

    // For comma-separated strings, split and check if any part matches
    // This handles fields like contributions that are stored as "Producer, Writer"
    if (normalized.includes(', ')) {
      const parts = normalized.split(', ').map((part) => part.trim());
      return parts.some((part) => this.appliedValues.has(part));
    }

    // For single values, check direct match
    return this.appliedValues.has(normalized);
  }

  public isFilterActive(): boolean {
    // Active on the *committed* selection — pending ticks must not filter the
    // table, or deferring the apply would be pointless.
    return this.appliedValues.size > 0;
  }

  public getModel(): CheckboxFilterModel | null {
    if (!this.isFilterActive()) {
      return null;
    }

    // If reverse mapper is provided, map display values back to API values
    let values = Array.from(this.appliedValues);
    if (this.params.reverseMapper) {
      values = values.map((v) => this.params.reverseMapper!(v));
    }

    return {
      values,
    };
  }

  public setModel(model: CheckboxFilterModel | null): void {
    // A programmatic set is authoritative, so it clears the pending edit too —
    // otherwise "Clear all filters" would empty the table's filters while
    // leaving ticked boxes behind in a popup that reopens out of sync.
    if (!model) {
      this.appliedValues.clear();
      this.pendingValues.clear();
      this.clearSearch();
      this.repartition();
      return;
    }

    this.appliedValues = new Set(model.values);
    this.pendingValues = new Set(model.values);
    this.clearSearch();
    this.repartition();
  }

  public afterGuiAttached(): void {
    this.guiAttached = true;
    // Restore the saved size now that the popup is in the DOM and the table
    // can be measured for clamping.
    this.applySavedSize?.();
    // Opening is a safe moment to float the selected values back to the top.
    this.repartition();
    this.searchInput?.focus();
  }

  public afterGuiDetached(): void {
    this.guiAttached = false;
    // Dismissing without applying discards the edit. A popup that reopens
    // showing six ticked boxes while the table is filtered by something else
    // is a trap; the text filter gets away with keeping its pending value
    // because dirty text is visible at a glance, but ticks aren't.
    this.handleCancel();
  }

  public onNewRowsLoaded(): void {
    this.updateAvailableValues();
  }

  public destroy(): void {
    // no-op
  }

  public refresh(): boolean {
    this.updateAvailableValues();
    return true;
  }

  // Method to update available values from external source (e.g., API)
  public setAvailableValues(values: string[]): void {
    // Apply value extractor if provided (e.g., extract year from dates)
    let processedValues = values;
    if (this.params.valueExtractor) {
      processedValues = values.map((v) => this.params.valueExtractor!(v));
    }

    // Apply value mapper if provided (e.g., "True" -> "Yes", "False" -> "No")
    if (this.params.valueMapper) {
      processedValues = processedValues.map((v) => this.params.valueMapper!(v));
    }

    // Remove duplicates after processing
    let deduplicatedValues = Array.from(new Set(processedValues));

    // Apply case-insensitive deduplication if enabled
    if (this.params.caseInsensitiveDeduplication) {
      deduplicatedValues = deduplicateCaseInsensitive(deduplicatedValues);
    }

    this.values = deduplicatedValues;
    this.values.sort((a, b) => a.localeCompare(b));
    this.filteredValues = [...this.values];

    this.pruneSelectionsToAvailableValues();
    this.renderAfterAvailableValuesChanged();
  }

  private createSearchInput() {
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = 'Search';
    this.searchInput.className =
      'tw-rounded tw-bg-[#1d2023] tw-border tw-border-[#30353b] tw-px-2 tw-py-1';
    this.searchInput.addEventListener('input', () => {
      this.applySearch(this.searchInput.value);
    });
    this.searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleApply();
      }
    });
    this.gui.appendChild(this.searchInput);
  }

  private createSelectedContainer() {
    this.selectedContainer = document.createElement('div');
    this.selectedContainer.className =
      'tw-flex tw-flex-col tw-gap-1 tw-pb-2 tw-border-b tw-border-[#30353b] tw-max-h-32 tw-overflow-auto';
    this.gui.appendChild(this.selectedContainer);
  }

  private createResetButton() {
    const resetContainer = document.createElement('div');
    resetContainer.className = 'tw-flex tw-justify-end tw-pb-2';

    this.resetButton = document.createElement('button');
    this.resetButton.type = 'button';
    this.resetButton.textContent = 'Reset';
    this.resetButton.className =
      'tw-text-xs tw-text-blue-400 hover:tw-text-blue-300 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 tw-px-1';
    this.resetButton.style.display = 'none';
    this.resetButton.addEventListener('click', () => {
      // Reset unticks everything but stays pending like every other edit in
      // this popup — one exception to "nothing applies until Apply" and users
      // stop trusting the rule. Repartitioning here is fine: it's a deliberate
      // wipe, not a mid-click-run reshuffle.
      this.pendingValues.clear();
      this.clearSearch();
      this.repartition();
    });

    resetContainer.appendChild(this.resetButton);
    this.gui.appendChild(resetContainer);
  }

  private createFooterButtons() {
    const footer = document.createElement('div');
    // `flex-shrink: 0` keeps the footer at full height when the user drags the
    // box smaller, and the right padding clears the 16px resize handle that
    // `makeFilterBoxResizable` pins to the bottom-right corner.
    footer.className =
      'tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-pt-2 tw-pr-4 tw-border-t tw-border-[#30353b]';

    this.cancelButton = document.createElement('button');
    this.cancelButton.type = 'button';
    this.cancelButton.textContent = 'Cancel';
    this.cancelButton.className =
      'tw-text-xs tw-text-neutral-300 hover:tw-text-white tw-cursor-pointer tw-bg-transparent tw-border tw-border-[#30353b] tw-rounded tw-px-3 tw-py-1 tw-transition-colors';
    this.cancelButton.addEventListener('click', () => {
      this.handleCancel();
      this.params.api.hidePopupMenu();
    });

    this.applyButton = document.createElement('button');
    this.applyButton.type = 'button';
    this.applyButton.textContent = 'Apply';
    this.applyButton.className =
      'tw-text-xs tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-cursor-pointer tw-border-none tw-rounded tw-px-3 tw-py-1 tw-transition-colors';
    this.applyButton.addEventListener('click', () => {
      this.handleApply();
    });

    footer.appendChild(this.cancelButton);
    footer.appendChild(this.applyButton);
    this.gui.appendChild(footer);

    this.updateButtons();
  }

  private handleApply() {
    if (this.hasPendingChanges()) {
      this.appliedValues = new Set(this.pendingValues);
      this.params.filterChangedCallback();
    }
    this.clearSearch();
    this.repartition();
    this.params.api.hidePopupMenu();
  }

  private handleCancel() {
    this.pendingValues = new Set(this.appliedValues);
    this.clearSearch();
    this.repartition();
  }

  /** The search narrows one edit session, so every way a session ends —
   *  Apply, Cancel, Reset, dismissal (via `handleCancel`), or a programmatic
   *  `setModel` — clears it. A query that outlives the session silently hides
   *  options the next time the popup opens. Callers repartition afterwards,
   *  which re-renders from the restored `filteredValues`. */
  private clearSearch() {
    if (!this.searchInput) {
      return;
    }
    this.searchInput.value = '';
    this.filteredValues = [...this.values];
  }

  private hasPendingChanges(): boolean {
    if (this.pendingValues.size !== this.appliedValues.size) {
      return true;
    }
    for (const value of this.pendingValues) {
      if (!this.appliedValues.has(value)) {
        return true;
      }
    }
    return false;
  }

  private updateButtons() {
    if (this.applyButton) {
      const dirty = this.hasPendingChanges();
      this.applyButton.disabled = !dirty;
      this.applyButton.style.opacity = dirty ? '1' : '0.5';
      this.applyButton.style.cursor = dirty ? 'pointer' : 'not-allowed';
    }

    if (this.resetButton) {
      this.resetButton.style.display =
        this.pendingValues.size > 0 ? 'block' : 'none';
    }
  }

  private createListContainer() {
    this.listContainer = document.createElement('div');
    this.listContainer.className =
      'tw-max-h-60 tw-overflow-auto tw-flex tw-flex-col tw-gap-1';
    this.gui.appendChild(this.listContainer);
  }

  private updateAvailableValues() {
    // If availableValues are provided via params, use those instead of scanning grid
    if (this.params.availableValues && this.params.availableValues.length > 0) {
      let processedValues = [...this.params.availableValues];

      // Apply value extractor if provided (e.g., extract year from dates)
      if (this.params.valueExtractor) {
        processedValues = processedValues.map((v) =>
          this.params.valueExtractor!(v)
        );
      }

      // Apply value mapper if provided (e.g., "True" -> "Yes", "False" -> "No")
      if (this.params.valueMapper) {
        processedValues = processedValues.map((v) =>
          this.params.valueMapper!(v)
        );
      }

      // Remove duplicates after processing
      let deduplicatedValues = Array.from(new Set(processedValues));

      // Apply case-insensitive deduplication if enabled
      if (this.params.caseInsensitiveDeduplication) {
        deduplicatedValues = deduplicateCaseInsensitive(deduplicatedValues);
      }

      this.values = deduplicatedValues;
      this.values.sort((a, b) => a.localeCompare(b));
      this.filteredValues = [...this.values];
    } else {
      // Fallback to scanning grid (original behavior)
      const valuesMap = new Map<string, string>(); // Map<lowercase, first occurrence>

      this.params.api.forEachNode((node) => {
        if (!node.data || (node.data as any).kind === 'detail') {
          return;
        }

        const value = this.getValue({ node } as IDoesFilterPassParams);
        let normalized = this.normalizeValue(value);

        // Apply value extractor if provided
        if (normalized && this.params.valueExtractor) {
          normalized = this.params.valueExtractor(normalized);
        }

        // Apply value mapper if provided
        if (normalized && this.params.valueMapper) {
          normalized = this.params.valueMapper(normalized);
        }

        const finalValue = normalized ?? BLANK_KEY;

        if (this.params.caseInsensitiveDeduplication) {
          const lower = finalValue.toLowerCase();
          if (!valuesMap.has(lower)) {
            valuesMap.set(lower, finalValue);
          }
        } else {
          valuesMap.set(finalValue, finalValue);
        }
      });

      this.values = Array.from(valuesMap.values());
      this.values.sort((a, b) => a.localeCompare(b));
      this.filteredValues = [...this.values];
    }

    this.pruneSelectionsToAvailableValues();
    this.renderAfterAvailableValuesChanged();
  }

  // Drop selections whose value no longer exists. Both sets are pruned, as the
  // single set this replaced always was.
  private pruneSelectionsToAvailableValues() {
    const available = new Set(this.values);
    this.pendingValues = new Set(
      Array.from(this.pendingValues).filter((value) => available.has(value))
    );
    this.appliedValues = new Set(
      Array.from(this.appliedValues).filter((value) => available.has(value))
    );
  }

  // New available values always need a re-render, but they must not reshuffle
  // a list the user is part-way through ticking.
  private renderAfterAvailableValuesChanged() {
    if (this.guiAttached && this.hasPendingChanges()) {
      this.renderOptions();
      return;
    }
    this.repartition();
  }

  // Recompute which options sit in the "Selected:" box, then redraw.
  private repartition() {
    this.partitionedValues = new Set(this.pendingValues);
    this.renderOptions();
  }

  private renderOptions() {
    // Clear containers
    this.selectedContainer.innerHTML = '';
    this.listContainer.innerHTML = '';

    // Separate filtered values into selected and unselected
    const selected: string[] = [];
    const unselected: string[] = [];

    // Placement comes from the partition snapshot, not the live selection, so
    // ticking a box never moves it. An option unticked mid-session therefore
    // stays in the "Selected:" box until the next repartition, which also
    // means a mis-click is easy to undo without hunting for the row again.
    this.filteredValues.forEach((value) => {
      if (this.partitionedValues.has(value)) {
        selected.push(value);
      } else {
        unselected.push(value);
      }
    });

    // Sort selected values
    selected.sort((a, b) => a.localeCompare(b));

    // Render selected values at the top with a label if there are any
    if (selected.length > 0) {
      const labelDiv = document.createElement('div');
      labelDiv.className =
        'tw-text-xs tw-text-neutral-400 tw-font-medium tw-mb-1';
      labelDiv.textContent = 'Selected:';
      this.selectedContainer.appendChild(labelDiv);

      selected.forEach((value) => {
        const label = this.createCheckboxLabel(value);
        this.selectedContainer.appendChild(label);
      });
    }

    // Render unselected values below
    unselected.forEach((value) => {
      const label = this.createCheckboxLabel(value);
      this.listContainer.appendChild(label);
    });

    this.updateButtons();
  }

  private createCheckboxLabel(value: string): HTMLElement {
    const label = document.createElement('label');
    label.className =
      'tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-white tw-cursor-pointer tw-min-w-0';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'tw-h-4 tw-w-4';
    checkbox.checked = this.pendingValues.has(value);
    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        this.pendingValues.add(value);
      } else {
        this.pendingValues.delete(value);
      }
      // Deliberately no `filterChangedCallback` and no `renderOptions` here:
      // the table waits for Apply, and the list must not move under the cursor
      // while the user is picking a second and third option.
      this.updateButtons();
    });

    const formatted = value === BLANK_KEY ? 'Blanks' : value;
    const optionLabel = document.createElement('span');
    optionLabel.textContent = formatted;
    optionLabel.className = 'tw-truncate tw-min-w-0 tw-flex-1';

    label.appendChild(checkbox);
    label.appendChild(optionLabel);

    return label;
  }

  private applySearch(value: string) {
    const query = value.toLowerCase();
    this.filteredValues = this.values.filter((option) => {
      const formatted = option === BLANK_KEY ? 'Blanks' : option;
      return formatted.toLowerCase().includes(query);
    });
    // Changing the search is a safe moment to repartition — the cursor is in
    // the search box, not part-way down the option list.
    this.repartition();
  }

  private getValue(params: IDoesFilterPassParams): any {
    const { colDef } = this.params;
    const valueGetter = colDef.filterValueGetter || colDef.valueGetter;

    if (valueGetter && typeof valueGetter === 'function') {
      return valueGetter({
        api: this.params.api,
        column: this.params.column,
        context: this.params.context,
        data: params.node.data,
        node: params.node,
      } as any);
    }

    if (colDef.field && params.node.data) {
      return (params.node.data as any)[colDef.field];
    }

    return null;
  }

  private normalizeValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.normalizeValue(item) ?? '').join(', ');
    }

    return String(value);
  }
}
